import React from "react";

import { formatDateOnly } from "../../../../utils/datetime";
import { chfPrecise } from "../../../../components/admin/companyFinance/financeFormat";
import { tenantDepositProviderLabel } from "../../unit/unitDetailHelpers";
import {
  depositSecurityState,
  depositTypeLabel,
} from "../../deposits/depositsLabels";
import {
  BigValue,
  CardNeutralState,
  MetaRow,
  OverviewCard,
  ShieldIcon,
  StatusPill,
} from "./overviewUi";

/**
 * `DepositBadge` tones (the deposits vocabulary) onto `StatusPill` tones (this
 * panel's). The WORDS come from the shared source; only the colour name is
 * translated, because the two badge components predate each other and unifying
 * them is a UI change, not a semantics one.
 */
const PILL_TONE = {
  positive: "emerald",
  warn: "amber",
  neutral: "slate",
  muted: "slate",
};

/** What is missing when a security is configured but unconfirmed. */
const PENDING_SUBTEXT = {
  insurance: "Versicherung noch nicht bestätigt",
  cash: "Kaution noch nicht erhalten",
  bank: "Depot noch nicht bestätigt",
};

/**
 * "Kaution" for the current tenancy.
 *
 * SCOPE. The deposit lives on the TENANCY (`tenancies.tenant_deposit_*`), not on
 * the tenant, and this card shows the CURRENT tenancy's — never a sum across
 * several. The label says so.
 *
 * NO "ZERTIFIKAT". The reference design shows a "Zertifikat: Vorhanden" row. No
 * such field exists: neither `tenancies` nor the tenancy-document category list
 * (`rent_contract`, `deposit_contract`, `deposit_payment_proof`,
 * `deposit_return_proof`, `handover_protocol`, `damage_evidence`,
 * `repair_invoice`, `other`) records a deposit certificate, and
 * `applications.loss_certificates` is Verlustscheine — a different thing
 * entirely. Deriving it from "some document exists" would be a guess printed as
 * a fact, so the row is omitted and the recorded receipt date takes its place.
 *
 * The state comes from the same precedence the Barkautionen KPI uses, so the
 * card and the dashboard cannot disagree.
 *
 * READ-ONLY, SINCE K4d. This card used to carry "Kaution bearbeiten" and
 * "Rückzahlungsdetails". Both moved to the individual tenancy card, because a
 * deposit belongs to a TENANCY and this card can only ever show one of them:
 * `selectCurrentTenancy` picks the running tenancy with the latest move-in, so
 * during an internal move the action silently edited one of two overlapping
 * contracts, and for a tenant whose tenancies are all reserved, cancelled or
 * ended it did not exist at all — leaving those deposits uneditable even
 * though the backend allows the correction.
 *
 * No replacement action is added here. `CurrentTenancyCard` sits beside this
 * one and already opens the Mietverhältnisse tab; a second link to the same
 * place is the duplicated affordance K4x.3a removed.
 */
export default function DepositCard({ deposit }) {
  // One source for the words, here and on /admin/kautionen.
  const meta = deposit ? depositSecurityState(deposit.state, deposit.type) : null;

  const hasFigure = deposit && Number.isFinite(deposit.amount) && deposit.amount > 0;
  const typeLabel = deposit && deposit.type ? depositTypeLabel(deposit.type) : null;
  const providerLabel =
    deposit && deposit.provider
      ? tenantDepositProviderLabel(deposit.provider)
      : null;

  return (
    <OverviewCard
      title="Kaution"
      icon={<ShieldIcon />}
      accent="emerald"
      testId="overview-deposit-card"
      action={
        meta ? (
          <StatusPill
            tone={PILL_TONE[meta.tone] || "slate"}
            testId="overview-deposit-status"
          >
            {meta.label}
          </StatusPill>
        ) : null
      }
    >
      {!deposit ? (
        <CardNeutralState
          testId="overview-deposit-empty"
          text="Ohne laufendes Mietverhältnis ist keine Kaution zugeordnet."
        />
      ) : (
        <>
          <BigValue testId="overview-deposit-amount">
            {hasFigure ? chfPrecise(deposit.amount) : "—"}
          </BigValue>
          {!hasFigure ? (
            <p className="m-0 mt-1 text-[11.5px] leading-snug text-muted-foreground">
              Kein Betrag erfasst.
            </p>
          ) : null}

          <div className="mt-3 divide-y divide-border">
            <MetaRow
              label="Typ"
              value={
                providerLabel && typeLabel
                  ? `${typeLabel} · ${providerLabel}`
                  : typeLabel
              }
            />
            {deposit.state === "returned" ? (
              <MetaRow
                // Names the COLUMN, not the state: the badge above already says
                // "Zurückbezahlt" or "Freigegeben" depending on the type, and a
                // near-miss spelling of one of them beside it read as a second,
                // slightly different fact.
                label="Rückgabe am"
                value={
                  deposit.returnedAt ? formatDateOnly(deposit.returnedAt) : null
                }
              />
            ) : (
              <MetaRow
                label="Erhalten am"
                value={
                  deposit.receivedAt ? formatDateOnly(deposit.receivedAt) : null
                }
              />
            )}
          </div>

          {deposit.state === "unresolved" ? (
            <p className="m-0 mt-3 text-[11.5px] leading-snug text-muted-foreground">
              Betrag erfasst, aber keiner Kautionsart eindeutig zugeordnet.
            </p>
          ) : null}

          {/* Detail view only: the badge says "Ausstehend", this says what is
              actually outstanding. The compact table has no room and no need. */}
          {deposit.state === "pending" ? (
            <p
              className="m-0 mt-3 text-[11.5px] leading-snug text-muted-foreground"
              data-testid="overview-deposit-pending-note"
            >
              {PENDING_SUBTEXT[deposit.type] || "Sicherheit noch nicht bestätigt"}
            </p>
          ) : null}
        </>
      )}
    </OverviewCard>
  );
}
